import React, { useMemo } from "react";
import type { BatchCase, BatchPhase } from "../../hooks/useBatchUpload";
import { batchMonitorSummary, batchProgressCounts } from "./batchUploadSummary";

interface BatchProgressStatsProps {
  phase: BatchPhase;
  cases: BatchCase[];
}

/** Aggregate progress breakdown shown above the phase-specific study list. */
export const BatchProgressStats: React.FC<BatchProgressStatsProps> = ({ phase, cases }) => {
  const summary = useMemo(() => batchMonitorSummary(phase, cases), [cases, phase]);
  const stats = useMemo(() => {
    const counts = batchProgressCounts(cases);
    return [
      { label: "Total studies", value: counts.total },
      { label: "Uploaded", value: counts.uploaded },
      { label: "Done processing", value: counts.processed },
      { label: "Errors", value: counts.errors, warn: counts.errors > 0 },
    ];
  }, [cases]);

  return (
    <section
      aria-label="Batch progress summary"
      data-testid="batch-progress-stats"
      className="mb-4 rounded border border-border bg-surface"
    >
      <div className="flex items-center justify-between gap-4 border-b border-border px-3.5 py-3">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-fg-dim">
            Overall progress
          </div>
          <div className="mt-1 text-sm font-medium text-fg">{summary.label}</div>
        </div>
        <div className="font-mono text-lg tabular-nums text-signal-text-tint">
          {summary.percent}%
        </div>
      </div>
      <div className="grid grid-cols-4 divide-x divide-border">
        {stats.map((stat) => (
          <div key={stat.label} className="px-3.5 py-3">
            <div
              className={`font-mono text-xl tabular-nums ${stat.warn ? "text-warn" : "text-fg"}`}
            >
              {stat.value}
            </div>
            <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.08em] text-fg-dim">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
      <div className="h-1 bg-surface-2" aria-hidden="true">
        <div
          data-testid="batch-overall-progress"
          className="h-full bg-signal transition-[width] duration-300"
          style={{ width: `${summary.percent}%` }}
        />
      </div>
    </section>
  );
};
